"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Search, Code, Star, Cpu, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight, Clock, Sparkles, Terminal, Play, Pause, RefreshCw, ChevronRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
const pipelineStages = [{
  id: 1,
  stageNumber: "01",
  title: "Free AI Checkup",
  shortTitle: "Visibility Audit",
  duration: "24-48 Hours",
  iconName: "Search",
  summary: "We analyze how ChatGPT, Claude, Gemini, and Perplexity perceive your business today.",
  detailLead: "We simulate multi-variant search queries across major generative AI engines to locate citation gaps, entity inaccuracies, and competitor stealing your market share.",
  bullets: "Audit 20+ AI models & search aggregators;Identify missing structured schemas & citations;Deliver actionable gap analysis report",
  feedTitle: "AI Audit Engine Log",
  feedStatus: "Analysis Complete",
  feedLog1: "[00:01] Querying ChatGPT-4o & Claude 3.5 Sonnet...",
  feedLog2: "[00:03] Unindexed brand entity detected in graph.",
  feedLog3: "[00:05] Audit complete: 3 critical citation gaps found.",
  actionText: "Get Free AI Visibility Checkup",
  actionLink: "/VisibilityCheckupPage"
}, {
  id: 2,
  stageNumber: "02",
  title: "Website Cleanup & Schema",
  shortTitle: "Site Optimization",
  duration: "Days 3 - 10",
  iconName: "Get started now",
  summary: "Refactoring web structure and injecting LLM-friendly JSON-LD structured schema.",
  detailLead: "AI models favor precise, machine-readable data over unstructured fluff. We optimize your website code, meta tags, and landing page response speed.",
  bullets: "Inject rich JSON-LD semantic schemas;Clean outdated HTML tags & heavy assets;Build dedicated AI response landing pages",
  feedTitle: "Schema Deployment Log",
  feedStatus: "Validation Active",
  feedLog1: "[00:02] Injecting Organization & LocalBusiness schema...",
  feedLog2: "[00:06] Synthesizing core FAQs into clean JSON-LD.",
  feedLog3: "[00:09] 100% Schema validation confirmed via test bots.",
  actionText: "Explore Structured Data",
  actionLink: "/StructuredDataPage"
}, {
  id: 3,
  stageNumber: "03",
  title: "Review System Setup",
  shortTitle: "Reputation Engine",
  duration: "Days 10 - 15",
  iconName: "Star",
  summary: "Automating authentic customer reviews that fuel AI citation confidence scores.",
  detailLead: "Generative engines weigh recent, high-volume review data heavily when recommending service providers. We set up automated feedback systems.",
  bullets: "Automate post-service SMS/email review requests;Filter feedback directly into internal dashboard;Sync high-star reviews to Google & AI data profiles",
  feedTitle: "Review Pipeline Log",
  feedStatus: "Automation Active",
  feedLog1: "[00:01] Connecting Google Business & CRM feedback API...",
  feedLog2: "[00:04] Automated feedback sequence triggered.",
  feedLog3: "[00:07] Average rating locked at 4.9/5 across profiles.",
  actionText: "View Reputation Solutions",
  actionLink: "/ReputationManagementPage"
}, {
  id: 4,
  stageNumber: "04",
  title: "LLM Indexing & Citations",
  shortTitle: "AI Indexing",
  duration: "Days 15 - 25",
  iconName: "Cpu",
  summary: "Pushing verified business entities directly into Perplexity, OpenAI, and Gemini databases.",
  detailLead: "We submit structured entity maps directly to vector databases and high-authority business indexes scraped constantly by AI web crawlers.",
  bullets: "Index core business entities on major AI data nodes;Configure Bing Webmaster & crawler permissions;Establish authoritative citation backlinks",
  feedTitle: "Vector Indexer Log",
  feedStatus: "Entities Synced",
  feedLog1: "[00:02] Submitting entity map to Bing & Perplexity graph...",
  feedLog2: "[00:05] Crawler permission verified for GPTBot & ClaudeBot.",
  feedLog3: "[00:08] Verified citation present in 4 core AI indexes.",
  actionText: "See AI Indexing Details",
  actionLink: "/LLMIndexingPage"
}, {
  id: 5,
  stageNumber: "05",
  title: "Monthly Tracking & Growth",
  shortTitle: "Reporting & Refinement",
  duration: "Ongoing Monthly",
  iconName: "BarChart3",
  summary: "Continuous AI visibility monitoring, answer tracking, and monthly strategic updates.",
  detailLead: "AI models update their training data and real-time search weights constantly. We continuously track your brand recommendations and optimize rankings.",
  bullets: "Track AI search positions vs direct local competitors;Perform monthly answer accuracy & citation checks;Iterative content tweaks for primary search slots",
  feedTitle: "Analytics Intelligence",
  feedStatus: "Monitoring Live",
  feedLog1: "[00:03] Running weekly benchmark across 50 key search queries...",
  feedLog2: "[00:06] Primary recommendation locked in 84% of queries.",
  feedLog3: "[00:10] Monthly visibility report published to client portal.",
  actionText: "View Analytics & Reports",
  actionLink: "/AnalyticsReportingPage"
}];
export default function Pipeline() {
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progressValue, setProgressValue] = useState(0);
  const [visitedStages, setVisitedStages] = useState<number[]>([0]);
  const shouldReduceMotion = useReducedMotion();
  const currentStage = pipelineStages[activeStageIndex];
  useEffect(() => {
    setVisitedStages(prev => prev.includes(activeStageIndex) ? prev : [...prev, activeStageIndex]);
  }, [activeStageIndex]);
  const renderIcon = (iconName: string, className: string = "size-5") => {
    switch (iconName) {
      case "Search":
        return <Search className={className} />;
      case "Code":
        return <Code className={className} />;
      case "Star":
        return <Star className={className} />;
      case "Cpu":
        return <Cpu className={className} />;
      case "BarChart3":
        return <BarChart3 className={className} />;
      default:
        return <Sparkles className={className} />;
    }
  };
  const handleNextStage = useCallback(() => {
    setActiveStageIndex(prev => (prev + 1) % pipelineStages.length);
    setProgressValue(0);
  }, []);
  const handlePrevStage = useCallback(() => {
    setActiveStageIndex(prev => (prev - 1 + pipelineStages.length) % pipelineStages.length);
    setProgressValue(0);
  }, []);
  const handleSelectStage = (index: number) => {
    setActiveStageIndex(index);
    setProgressValue(0);
  };
  useEffect(() => {
    if (!isPlaying) return;
    const stepTimeMs = 50;
    const totalDurationMs = 6000;
    const increment = stepTimeMs / totalDurationMs * 100;
    const timer = setInterval(() => {
      setProgressValue(prev => {
        if (prev >= 100) {
          handleNextStage();
          return 0;
        }
        return prev + increment;
      });
    }, stepTimeMs);
    return () => clearInterval(timer);
  }, [isPlaying, handleNextStage]);
  return <section id="interactivepipelinevisualization" data-nav="light" className="relative w-full overflow-hidden bg-background py-20 text-foreground md:py-28">
      {}
      <div className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-accent/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 size-80 rounded-full bg-[var(--accent-deep-red)]/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline" className="border-border bg-muted/50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-foreground">
                <Sparkles className="mr-1.5 size-3 text-[var(--accent-deep-red)]" />
                How ClearLine Works
              </Badge>
            </div>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Our 5-Stage AI Visibility Pipeline
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              When prospective clients search AI assistants for local experts, you should appear first. Here is our systematic process for putting your business in AI responses.
            </p>
          </div>
        </div>

        <Separator className="my-8 bg-border/60 md:my-12" />

        {}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card/60 p-4 shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="px-2.5 py-1 text-xs font-medium">
              Stage {currentStage.stageNumber} of 05
            </Badge>
            <span className="hidden text-sm font-semibold text-foreground sm:inline">
              {currentStage.title}
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden items-center gap-2 text-xs font-medium text-muted-foreground md:flex">
              <Clock className="size-3.5 text-muted-foreground" />
              <span>Pacing: {currentStage.duration}</span>
            </div>

            <Separator orientation="vertical" className="hidden h-5 md:block" />

            <Button variant="outline" size="sm" onClick={() => setIsPlaying(!isPlaying)} className="h-8 gap-1.5 px-3 text-xs" aria-label={isPlaying ? "Pause pipeline preview" : "Play pipeline preview"}>
              {isPlaying ? <>
                  <Pause className="size-3.5" />
                  <span>Pause</span>
                </> : <>
                  <Play className="size-3.5" />
                  <span>Auto-Advance</span>
                </>}
            </Button>

            <Button variant="ghost" size="sm" onClick={() => handleSelectStage((activeStageIndex + 1) % pipelineStages.length)} className="h-8 gap-1 px-2.5 text-xs text-muted-foreground hover:text-foreground">
              <span>Next</span>
              <ChevronRight className="size-3.5" />
            </Button>
          </div>
        </div>

        {}
        <div className="relative mb-12">
          {}
          <div className="hidden lg:block">
            <div className="relative flex items-center justify-between">
              {}
              <div className="absolute top-7 left-8 right-8 h-1 rounded-full bg-muted" />

              {}
              <div className="absolute top-7 left-8 h-1 rounded-full bg-[var(--accent-deep-red)] transition-all duration-500 ease-out" style={{
              width: `calc(${activeStageIndex / (pipelineStages.length - 1) * 100}% - 3rem)`
            }} />

              {}
              {pipelineStages.map((stage, i) => {
              const isActive = activeStageIndex === i;
              const isPassed = activeStageIndex > i;
              const isCompleted = !isActive && (activeStageIndex > i || visitedStages.includes(i));
              return <div key={stage.id} data-index={i} className="relative z-10 flex flex-col items-center">
                    {isCompleted && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="absolute -top-2 -right-1 z-20 flex size-5 items-center justify-center rounded-full bg-[var(--accent-deep-red)] text-white shadow-xs ring-2 ring-background">
                        <CheckCircle2 className="size-4" />
                      </motion.div>}

                    <Button variant={isActive ? "default" : isPassed ? "secondary" : "outline"} onClick={() => handleSelectStage(i)} className={`relative flex size-14 items-center justify-center rounded-xl p-0 font-bold transition-all duration-300 ${isActive ? "bg-[var(--accent-deep-red)] text-white shadow-md ring-4 ring-[var(--accent-deep-red)]/20 scale-110" : isPassed ? "border-[var(--accent-deep-red)]/40 bg-secondary text-foreground hover:border-[var(--accent-deep-red)]" : "border-border bg-card text-muted-foreground hover:border-border hover:text-foreground"}`} aria-label={`Jump to stage ${stage.stageNumber}: ${stage.title}`}>
                      <span className="text-base">{stage.stageNumber}</span>
                    </Button>

                    <div className="mt-3 text-center">
                      <p className={`text-xs font-semibold ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                        {stage.shortTitle}
                      </p>
                      <span className="text-[10px] text-muted-foreground/80">{stage.duration}</span>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="block lg:hidden">
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
              {pipelineStages.map((stage, i) => {
              const isActive = activeStageIndex === i;
              const isPassed = activeStageIndex > i;
              const isCompleted = !isActive && (activeStageIndex > i || visitedStages.includes(i));
              return <Button key={stage.id} data-index={i} variant={isActive ? "default" : "outline"} onClick={() => handleSelectStage(i)} className={`relative flex flex-col items-center justify-center rounded-lg p-2 h-auto text-center transition-all ${isActive ? "bg-[var(--accent-deep-red)] text-white shadow-xs ring-2 ring-[var(--accent-deep-red)]/30" : isPassed ? "border-border bg-muted/40 text-foreground" : "border-border bg-card text-muted-foreground"}`}>
                    {isCompleted && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="absolute -top-1 -right-1 z-10 flex size-4 items-center justify-center rounded-full bg-[var(--accent-deep-red)] text-white ring-1 ring-background">
                        <CheckCircle2 className="size-3" />
                      </motion.div>}
                    <span className="text-xs font-bold">{stage.stageNumber}</span>
                    <span className="mt-1 hidden text-[10px] sm:inline truncate max-w-full">
                      {stage.shortTitle}
                    </span>
                  </Button>;
            })}
            </div>

            {}
            <div className="mt-3">
              <Progress value={progressValue} className="h-1.5 w-full bg-muted" />
            </div>
          </div>
        </div>

        {}
        <AnimatePresence mode="wait">
          <motion.div key={currentStage.id} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: -15
        }} transition={{
          duration: 0.35,
          ease: "easeInOut"
        }}>
            <Card className="border-border bg-card shadow-md">
              <CardContent className="p-6 md:p-8 lg:p-10">
                <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">
                  {}
                  <div className="flex flex-col justify-between space-y-6 lg:col-span-7">
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="flex size-12 items-center justify-center rounded-lg bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)]">
                          {renderIcon(currentStage.iconName, "size-6")}
                        </div>
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Step {currentStage.stageNumber} Details
                          </span>
                          <h3 className="text-2xl font-bold text-card-foreground sm:text-3xl">
                            {currentStage.title}
                          </h3>
                        </div>
                      </div>

                      <p className="text-base font-medium leading-relaxed text-foreground md:text-lg">
                        {currentStage.summary}
                      </p>

                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {currentStage.detailLead}
                      </p>
                    </div>

                    {}
                    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Key Stage Deliverables:
                      </h4>
                      <ul className="space-y-2.5 text-sm text-foreground">
                        {currentStage.bullets.split(";").map((bullet, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5">
                            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[var(--accent-deep-red)]" />
                            <span>{bullet}</span>
                          </li>)}
                      </ul>
                    </div>

                    {}
                    <div className="flex flex-wrap items-center gap-4 pt-2">
                      <Button asChild size="lg" className="gap-2 text-white hover:opacity-90 transition-opacity" style={{
                      backgroundColor: "var(--accent-deep-red)"
                    }}>
                        <Link to="/VisibilityCheckupPage">
                          <span>Get Free AI Visibility Checkup</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>

                      <Button asChild variant="outline" size="lg">
                        <Link to="/ContactPage">
                          Book a Call
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {}
                  <div className="lg:col-span-5">
                    <div className="relative overflow-hidden rounded-xl border border-border bg-slate-950 p-5 font-mono text-xs text-slate-200 shadow-xl dark:bg-slate-950">
                      {}
                      <div className="mb-4 flex items-center justify-between border-b border-slate-800 pb-3">
                        <div className="flex items-center gap-2">
                          <Terminal className="size-4 text-emerald-400" />
                          <span className="font-semibold text-slate-300">{currentStage.feedTitle}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="relative flex size-2">
                            <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                          </span>
                          <span className="text-[10px] text-emerald-400">{currentStage.feedStatus}</span>
                        </div>
                      </div>

                      {}
                      <div className="space-y-3 py-1">
                        <div className="rounded bg-slate-900/80 p-2.5 border border-slate-800/80">
                          <p className="text-slate-400">{currentStage.feedLog1}</p>
                        </div>
                        <div className="rounded bg-slate-900/80 p-2.5 border border-slate-800/80">
                          <p className="text-amber-300">{currentStage.feedLog2}</p>
                        </div>
                        <div className="rounded bg-slate-900/80 p-2.5 border border-slate-800/80">
                          <p className="text-emerald-400">{currentStage.feedLog3}</p>
                        </div>
                      </div>

                      {}
                      <div className="mt-4 flex items-center justify-between border-t border-slate-800/80 pt-3 text-[10px] text-slate-500">
                        <span>ClearLine Core Protocol v2.4</span>
                        <div className="flex items-center gap-1">
                          <RefreshCw className="size-3 animate-spin text-slate-400" />
                          <span>Live Syncing</span>
                        </div>
                      </div>
                    </div>

                    {}
                    {isPlaying && <div className="mt-4 hidden lg:block">
                        <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                          <span>Auto-Advancing Stage</span>
                          <span>{Math.round(progressValue)}%</span>
                        </div>
                        <Progress value={progressValue} className="h-1.5 w-full bg-[var(--accent-deep-red)]/20 [&>[data-slot=progress-indicator]]:bg-[var(--accent-deep-red)]" />
                      </div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {}
        <div className="mt-12 flex flex-col items-center justify-between gap-6 rounded-xl border border-border bg-muted/40 p-6 text-center sm:flex-row sm:text-left md:p-8">
          <div>
            <h4 className="text-lg font-bold text-foreground">
              Ready to see where your business stands in AI search?
            </h4>
            <p className="mt-1 text-sm text-muted-foreground">
              We provide a complete audit within 48 hours—100% free with no obligation.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Button asChild size="lg" className="text-white hover:opacity-90 transition-opacity" style={{
            backgroundColor: "var(--accent-deep-red)"
          }}>
              <Link to="/VisibilityCheckupPage">
                Get Free AI Checkup
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}